import { alpha, Box, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import SurfaceCard from "../../Common/SurfaceCard";
import SurfaceSection from "../../Common/SurfaceSection";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import useReportsService from "../../../services/useReportsService";
import { MonthlyRevenueReportEntry } from "../../../types/Reports";
import { getMonths } from "../../../utils/time";
import AmountInput from "./AmountInput";
import { formatCurrency, formatNumber, formatPercentage } from "../../../utils/formatNumber";
import ValueProgressIndicator from "../../Common/Charts/ValueProgressIndicator";
import useToast, { ToastPosition, ToastType } from "../../../hooks/useToast";
import LineChart from "../../Common/Charts/LineChart";
import { Bar } from "react-chartjs-2";

interface MonthlyRevenueReportDialogProps {
    open: boolean;
    onClose: () => void;
}

interface MontlhyAmountInputProps {
    amount: number;
    year: number;
    month: number;
    handleSubmit: (oldVal: number, newVal: number, year: number, month: number) => void;
}

const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);


const DiagramsContainer = styled(Box)(({ theme }) => `
    display: grid;
    gap: ${theme.spacing(2)};
    grid-template-columns: 1fr 1fr;

    ${theme.breakpoints.down('md')} {
        flex: none;
        grid-template-columns: 1fr;
        min-height: auto;
    }

    ${theme.breakpoints.up('md')} {
        flex: 1;
        min-height: 0;
    }
`);

const DiagramWrapper = styled(Box)`
    flex: 1;
    min-height: 0;
`;

const ReportContainer = styled(Box)(({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};

    ${theme.breakpoints.up('md')} {
        flex: 1;
        min-height: 0;
    }

    ${theme.breakpoints.down('md')} {
        flex: none;
        min-height: auto;
    }
`);

const TableContainer = styled(Box)`
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
`;

const MonthlyAmountInput = ({ amount, year, month, handleSubmit }: MontlhyAmountInputProps) => {
    const [value, setValue] = useState<number>(amount);

    const handleAmountChange = (amount: number) => {
        if (isNaN(amount) || amount < 0) return;

        setValue(amount);
    };

    const handleInputSubmit = () => {
        handleSubmit(amount, value, year, month);
    };

    return (
        <AmountInput
            value={value}
            handleInputChange={handleAmountChange}
            handleSubmit={handleInputSubmit}
            key={`${year}-${month}-input`}
        />
    )
}

const MonthlyRevenueReportDialog = ({ open, onClose }: MonthlyRevenueReportDialogProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [reportData, setReportData] = useState<MonthlyRevenueReportEntry | null>(null);
    const lineChartRef = useRef<HTMLDivElement>(null);

    const { showToast } = useToast();
    const { t } = useTranslation("common");
    const theme = useTheme();
    const { fetchMonthlyRevenueReportData, updateMonthlyRevenueReport } = useReportsService();

    useEffect(() => {
        if (!open) return;

        setLoading(true);
        fetchMonthlyRevenueReportData()
            .then((data) => {
                setReportData(data);
            })
            .catch(() => {
                showToast({
                    message: t("common.unknownError"),
                    type: ToastType.ERROR,
                    position: ToastPosition.TOP_CENTER,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [open, fetchMonthlyRevenueReportData, t, showToast]);

    const renderMonthlyIncomeChart = () => {
        const chartData = reportData ? Object.keys(reportData).flatMap((year) => {
            const monthsObj = reportData[+year];
            return Object.keys(monthsObj).map((monthNum) => ({
                label: `${String(monthNum).padStart(2, '0')}/${year}`,
                value: monthsObj[+monthNum].amount ?? 0,
            }));
        }) : [];

        return (
            <Bar
                data={{
                    labels: chartData.map(item => item.label),
                    datasets: [{
                        label: t("reports.monthlyRevenueReport.totalMonthlyIncome"),
                        data: chartData.map(item => item.value),
                        backgroundColor: alpha(theme.palette.success.main, 0.6),
                    }]
                }}
                options={{
                    // todo use actual locale
                    locale: "ro",
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: false,
                            position: 'top' as const,
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    return `${formatCurrency(context.parsed.y ?? 0)}`;
                                }
                            }
                        }
                    },
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: true,
                                maxRotation: 0,
                                minRotation: 0,
                                color: theme.palette.text.primary,
                            },
                            grid: {
                                color: theme.palette.divider,
                            },
                        },
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: theme.palette.text.primary,
                                callback: (value) => {
                                    return formatNumber(Number(value));
                                }
                            },
                            grid: {
                                color: theme.palette.divider,
                            },
                        },
                    },
                }}
            />
        )
    };

    const renderMediumValueSpentChart = () => {
        const chartData = reportData ? Object.keys(reportData).flatMap((year) => {
            const monthsObj = reportData[+year];
            return Object.keys(monthsObj).map((monthNum) => {
                return {
                    label: `${String(monthNum).padStart(2, '0')}/${year}`,
                    value: monthsObj[+monthNum].avgPerEntry ?? 0,
                };
            });
        }) : [];

        return (
            <LineChart
                parentRef={lineChartRef}
                colorPalette={[theme.palette.primary.light]}
                topBottomPadding={16}
                leftRightPadding={16}
                data={{
                    xAxis: {
                        data: chartData.map(item => item.label),
                        scaleType: 'point'
                    },
                    series: [{
                        id: 'monthly-revenue',
                        label: t("reports.monthlyRevenueReport.mediumValueSpent"),
                        data: chartData.map(item => item.value),
                        area: true,
                        valueFormatter: (value) => formatCurrency(value ?? 0),
                    }]
                }}
            />
        )
    }

    const handleAmountChange = async (oldVal: number, newVal: number, year: number, month: number) => {
        if (oldVal !== newVal) {
            if (year && month) {
                try {
                    const success = await updateMonthlyRevenueReport(year, month, newVal);
                    if (success) {
                        const data = await fetchMonthlyRevenueReportData();
                        setReportData(data);
                        showToast({
                            message: t("reports.monthlyRevenueReport.reportUpdatedSuccessfully"),
                            type: ToastType.SUCCESS,
                            position: ToastPosition.TOP_CENTER,
                        });
                    } else {
                        showToast({
                            message: t("common.unknownError"),
                            type: ToastType.ERROR,
                            position: ToastPosition.TOP_CENTER,
                        });
                    }
                } catch {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                        position: ToastPosition.TOP_CENTER,
                    });
                }
            }
        }
    }

    const renderTable = () => {
        const monthNames = getMonths(t);

        const baseTable = (
            <SurfaceSection sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("reports.monthlyRevenueReport.month")}</TableCell>
                                <TableCell>{t("reports.monthlyRevenueReport.totalMonthlyIncome")}</TableCell>
                                <TableCell>{t("reports.monthlyRevenueReport.noOfCars")}</TableCell>
                                <TableCell>{t("reports.monthlyRevenueReport.mediumValueSpent")}</TableCell>
                                <TableCell>{t("reports.monthlyRevenueReport.incomeEvolution")}</TableCell>
                                <TableCell>{t("reports.monthlyRevenueReport.mediumValueSpentEvolution")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData ? (
                                Object.keys(reportData).sort((a, b) => Number(b) - Number(a)).map((year) => {
                                    const monthsObj = reportData[+year];

                                    return Object.keys(monthsObj).sort((a, b) => Number(b) - Number(a)).map((monthNum) => {
                                        const entries = monthsObj[+monthNum]?.entries ?? 0;
                                        const monthLabel = monthNames[+monthNum - 1] || String(monthNum);
                                        const value = reportData[+year][+monthNum];

                                        return (
                                            <TableRow key={`${year}-${monthNum}`}>
                                                <TableCell>{monthLabel} {year}</TableCell>
                                                <TableCell>
                                                    <MonthlyAmountInput
                                                        amount={value.amount ?? 0}
                                                        year={+year}
                                                        month={+monthNum}
                                                        handleSubmit={handleAmountChange}
                                                    />
                                                </TableCell>
                                                <TableCell>{entries}</TableCell>
                                                <TableCell>{value.avgPerEntry ? formatCurrency(value.avgPerEntry) : '-'}</TableCell>
                                                <TableCell>
                                                    {
                                                        value.percentAmountComparedToLastMonth ?
                                                            <ValueProgressIndicator
                                                                value={value.percentAmountComparedToLastMonth}
                                                                showSign={true}
                                                                formatFunction={(val) => formatPercentage(val, false, 2)}
                                                            />
                                                            : '-'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        value.diffAvgComparedToLastMonth ?
                                                            <ValueProgressIndicator
                                                                value={value.diffAvgComparedToLastMonth}
                                                                showSign={true}
                                                                formatFunction={(val) => formatCurrency(val)}
                                                            />
                                                            : '-'
                                                    }
                                                </TableCell>
                                            </TableRow>
                                        );
                                    });
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6}>{t("reports.common.noData")}</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </SurfaceSection>
        );

        if (!reportData) {
            return baseTable;
        }

        return baseTable;
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {t("reports.monthlyRevenue.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: { xs: 'auto', md: 'hidden' },
                    gap: 2,
                }}>

                <Typography>
                    {t("reports.monthlyRevenue.subtitle")}
                </Typography>

                {loading ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <ReportContainer>
                        <DiagramsContainer>
                            {/* todo check if fixed height is needed */}
                            <SurfaceCard
                                title={t("reports.monthlyRevenueReport.totalMonthlyIncome")}
                                sx={{
                                    overflow: 'hidden',
                                    [theme.breakpoints.down('md')]: { height: theme.spacing(37.5) },
                                }}
                            >
                                <DiagramWrapper>
                                    {renderMonthlyIncomeChart()}
                                </DiagramWrapper>
                            </SurfaceCard>
                            <SurfaceCard
                                title={t("reports.monthlyRevenueReport.mediumValueSpent")}
                                sx={{
                                    overflow: 'hidden',
                                    [theme.breakpoints.down('md')]: { height: theme.spacing(37.5) },
                                }}
                            >
                                <DiagramWrapper ref={lineChartRef}>
                                    {renderMediumValueSpentChart()}
                                </DiagramWrapper>
                            </SurfaceCard>
                        </DiagramsContainer>
                        <Box sx={{ minHeight: { xs: 400, md: 0 }, flex: { xs: 'none', md: 1 } }}>
                            {renderTable()}
                        </Box>
                    </ReportContainer>)}
            </DialogContent >
        </Dialog >
    );
};

export default MonthlyRevenueReportDialog;
